import type Plan from '@/classes/Plan'
import { OapProvider } from '@/data/oapProviders'
import { CorporateAffiliation } from '@/enums/CorporateAffiliation'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { useCalculatorStore } from '@/stores/calculator'
import { useCartStore } from '@/stores/cart'
import { useSessionStore } from '@/stores/session'
import { createMessage, type FormKitNode, getNode } from '@formkit/core'
import {
	ErrorCode,
	Frequency,
	PaymentType,
	type Scenario,
	ScenarioErrorBag,
	type ScenarioOutput,
	State,
	Term,
} from '@precoa/calculator-sdk'
import { DateTime } from 'luxon'
import { computed, ref, watch } from 'vue'

export function usePaymentOptions() {
	const { t } = usePresentationLocale()
	const cartStore = useCartStore()

	type TermOption = {
		term: Term
		label: string
		payment: number
		formattedPayment: string
	}

	// Map Term to number of years
	const termYearValues: Partial<Record<Term, number>> = {
		[Term.ThreeYear]: 3,
		[Term.FiveYear]: 5,
		[Term.TenYear]: 10,
		[Term.TwentyYear]: 20,
	}

	/**
	 * Get the label for the given term
	 * @param {Term} term The term to get the label for
	 * @returns {string} The label for the given term
	 */
	function getTermLabel(term: Term): string {
		const years = termYearValues[term]
		if (years) {
			return t('payment.term.termYears', { number: years })
		}
		if (term === Term.SinglePay) {
			return t('payment.term.payInFull')
		}
		if (term === Term.TerminalIllness) {
			return t('payment.term.terminalIllness')
		}
		return term.toString()
	}

	const {
		calculateCartTotals,
		formatPrice,
		mockProductOap,
		useOapPrice,
		oapDetails,
		mockProductEverplans,
	} = useMerchandise()
	const sessionStore = useSessionStore()
	const calculator = useCalculatorStore().getCalculator()

	const isSepioCarrier = computed(
		() => oapDetails.value?.carrier === OapProvider.SEPIO_GUARD
	)

	/** Is the session's funeral home part of the Carriage corporate group */
	const isCarriageFuneralHome = computed(
		() =>
			sessionStore.activeFh?.corporateAffiliation ===
			CorporateAffiliation.Carriage
	)

	function getPlanPaymentOptions(plan: Plan) {
		// The target payment the user wants to pay
		const targetPayment = ref<number | undefined>()

		// The total funeral amount the user can afford based on the target payment
		const targetTotal = ref<number | null>(null)

		// Show the adjust payment inputs
		const showAdjustInputs = ref(false)

		// Errors for the main scenario
		const mainErrors = ref<string[]>([])

		// Is this a terminal illness plan
		const isTerminalIllness = computed(() => {
			return plan?.insurance?.term === Term.TerminalIllness
		})

		// Is OAP included in this plan
		const oapIncluded = computed(() => plan.oapIncluded)

		// Is Everplans included in this plan
		const everplansIncluded = computed(() => plan.everplansIncluded)

		// The selected payment type
		const selectedPaymentType = computed(() => plan?.insurance?.paymentType)

		/** Result of the main scenario calculation */
		const scenarioOutput = computed(() => calculatePayment({}))

		// The oap price based on the oap payment frequency and initial oap fixed price value
		const oapPrice = useOapPrice(plan)

		/** oapPlanProvider is updated when the funeralHome updates */
		const oapPlanProvider = useOapPlanProvider(
			sessionStore.activeOapProvider ?? null
		)

		/** Currently selected oapPlan */
		const selectedOapPlan = computed(() =>
			oapPlanProvider.getPlanById(plan.oapPayment.paymentPlanId)
		)
		/** Translation of "Annual" */
		const annualTranslated = computed(() =>
			t('payment.paymentFrequencyOptions.annual')
		)

		/** Translation of "First 3 Months" */
		const firstThreeMonthsTranslated = computed(() =>
			t('payment.label.firstThreeMonths')
		)

		/** Translation of "First Year" */
		const firstYearTranslated = computed(() => t('payment.label.firstYear'))

		/** Translation of "Monthly" */
		const monthlyTranslated = computed(() =>
			t('payment.paymentFrequencyOptions.monthly')
		)

		/** Translation of "Ongoing" */
		const ongoingTranslated = computed(() => t('payment.label.ongoing'))

		/** Translation of "Quarterly" */
		const quarterlyTranslated = computed(() =>
			t('payment.paymentFrequencyOptions.quarterly')
		)

		/** Translation of "Semi'Annual" */
		const semiAnnualTranslated = computed(() =>
			t('payment.paymentFrequencyOptions.semiAnnual')
		)

		/** Translation of "3 Months" */
		const threeMonthsTranslated = computed(() => t('payment.label.threeMonths'))

		/** Translation of "12 Months" */
		const twelveMonthsTranslated = computed(() =>
			t('payment.label.twelveMonths')
		)

		/** The total amount for a full year or 3 months of oap based on the oap payment frequency */
		const fullMultiPayOapAmount = computed(() => {
			const price = oapPrice.value
			const count = selectedOapPlan.value?.paymentCount ?? 1
			return price * count
		})

		// The cost of Everplans
		const everplansPrice = computed(() =>
			cartStore.cart[plan.id]?.includesFreeEverplansPremium
				? 0
				: (mockProductEverplans?.value?.fixedPrice ?? 299)
		)

		// Keep Due Today Everplans visible after opt-out when it was included at setup.
		// No-cost packages hide the row once Everplans is removed so it does not stay greyed out.
		const wasEverplansIncludedAtSetup = ref(!!plan.everplansIncluded)
		const wasEverplansInitiallyIncluded = computed(() => {
			const hasFreeEverplans =
				!!cartStore.cart[plan.id]?.includesFreeEverplansPremium

			if (hasFreeEverplans) {
				return !!plan.everplansIncluded
			}

			return wasEverplansIncludedAtSetup.value
		})

		// The initial oapIncluded value
		const wasOapInitiallyIncluded = ref(!!plan.oapIncluded)

		// The total funeral amount for the plan
		const totalFuneralAmount = computed(() => {
			if (!scenarioOutput.value?.isValid) return 0

			const base = scenarioOutput.value.totalFaceAmt ?? 0

			const oapAmount = oapIncluded.value
				? singlePay.value
					? (mockProductOap.value?.fixedPrice ?? 0)
					: fullMultiPayOapAmount.value
				: 0

			const everplansAmount = everplansIncluded.value
				? (everplansPrice.value ?? 0)
				: 0

			return base + oapAmount + everplansAmount
		})

		// The total amount paid over time
		const totalPaidOverTime = computed(() => {
			if (!scenarioOutput.value?.isValid) return 0
			return scenarioOutput.value.totalPaidAmt
		})

		// The monthly payment amount for the plan without down payment
		const monthlyPayment = computed(() => {
			if (!scenarioOutput.value?.isValid) return 0
			return scenarioOutput.value.multiPayPeriodicAmt
		})

		/** Is OAP frequency a three month payment */
		const isThreeMonthOap = computed(
			() => selectedOapPlan.value?.paymentCount === 3
		)

		/** Is OAP frequency a monthly payment */
		const isMonthlyOap = computed(
			() => selectedOapPlan.value?.paymentCount === 12
		)

		/** Is OAP frequency a single payment */
		const isPayInFullOap = computed(
			() => selectedOapPlan.value?.paymentCount === 1
		)

		/** The amount of the first payment */
		const firstPaymentAmount = computed(() => {
			if (singlePay.value) {
				return calculateCartTotals(plan).funeralTotal
			}

			if (plan.insurance.downPayment) {
				return 0
			}

			return monthlyPayment.value
		})

		/** The total amount due today based on scenario output and oap inclusion */
		const totalDueTodayAmount = computed(() => {
			if (!scenarioOutput.value?.isValid) return 0

			const base = scenarioOutput.value.totalDueTodayAmt ?? 0
			const oap = oapIncluded.value ? oapPrice.value : 0
			const everplans = everplansIncluded.value ? everplansPrice.value : 0

			return base + oap + everplans
		})

		/** Whether the cart contains items **/
		const planHasNoCartItems = computed(
			() =>
				(!plan.cartItems || plan.cartItems.length === 0) &&
				(!plan.customCartItems || plan.customCartItems.length === 0)
		)

		/** Error message for when there are no cart items in the plan */
		const cartItemsError = t('paymentOptionsSlide.errors.noCartItemsInPlan')

		// The funeral amount based on current cart totals
		const baseAmount = ref<number>(calculateCartTotals(plan).funeralTotal)

		// The funeral amount based user input (defaults to base amount)
		const localFuneralAmount = ref<number | undefined>(baseAmount.value)

		/**
		 * FormKit validation rule to check if the funeral amount is valid based on the current scenario.
		 * If the scenario is invalid (funeral amount is out of range based on state rules), an error is set.
		 * If the scenario is valid, any previous error is cleared.
		 */
		function funeralAmountRule(node: FormKitNode): boolean {
			const baseScenario = scenario.value
			if (!baseScenario) return true

			const funeralAmount = Number(node.value)
			const inputScenario: Scenario = {
				...baseScenario,
				funeralAmount,
			}

			try {
				const result = calculator.calculate(inputScenario)
				if (!result.isValid) {
					const allErrors = Object.values(result.errors).flat()

					const error = allErrors.find(
						(error: any) =>
							error.property === 'funeralAmount' &&
							(error.code === ErrorCode.MIN_FUNERAL_AMOUNT ||
								error.code === ErrorCode.MAX_FUNERAL_AMOUNT)
					)
					if (error) {
						node.store.set(
							createMessage({
								key: 'funeralAmountRule',
								value: error.message,
							})
						)
					}
				} else {
					node.store.set(createMessage({ key: 'funeralAmountRule', value: '' }))
				}
				return true
			} catch (e) {
				node.store.set(
					createMessage({
						key: 'funeralAmountRule',
						value: 'Unexpected validation error.',
					})
				)
				return true
			}
		}

		/**
		 * Sets the local funeral amount value, but only if the input
		 * value is a valid number. If the input is invalid, the value
		 * is set to undefined.
		 */
		const funeralAmountInput = computed<number | undefined>({
			get: () => localFuneralAmount.value,
			set: (val) => {
				localFuneralAmount.value =
					typeof val === 'number' && !isNaN(val) ? val : undefined
			},
		})

		/**
		 * Resets the local funeral amount to the base amount when the input field
		 * is blurred and no value is provided.
		 */
		function handleFuneralAmountBlur() {
			if (localFuneralAmount.value === undefined) {
				localFuneralAmount.value = baseAmount.value
			}
		}

		// Ensure validation runs even if input is empty
		funeralAmountRule.skipEmpty = false

		/**
		 * FormKit validation rule to check if the down payment is valid based on the current scenario.
		 * If the scenario is invalid (down payment is under $500 or the total funeral amount minus the down payment is less than $500), an error is set.
		 * If the scenario is valid, any previous error is cleared.
		 */
		function downPaymentRule(node: FormKitNode): boolean {
			const baseScenario = scenario.value
			if (!baseScenario) return true

			const downPayment = Number(node.value)
			if (isNaN(downPayment)) {
				node.store.set(createMessage({ key: 'downPaymentRule', value: '' }))
				return true
			}

			const inputScenario: Scenario = {
				...baseScenario,
				downPayment,
			}

			try {
				const result = calculator.calculate(inputScenario)
				if (!result.isValid) {
					const allErrors = Object.values(result.errors).flat()
					const error = allErrors.find(
						(error: any) =>
							error.property === 'downPayment' &&
							error.code === ErrorCode.INVALID_DOWN_PAYMENT
					)
					if (error) {
						node.store.set(
							createMessage({ key: 'downPaymentRule', value: error.message })
						)
					}
				} else {
					node.store.set(createMessage({ key: 'downPaymentRule', value: '' }))
				}
				return true
			} catch (e) {
				node.store.set(
					createMessage({
						key: 'downPaymentRule',
						value: 'Unexpected validation error.',
					})
				)
				return true
			}
		}
		downPaymentRule.skipEmpty = false

		// If there is a down payment suggestion from the calculator apply it on blur
		function handleDownPaymentBlur() {
			const baseScenario = scenario.value
			if (!baseScenario) return

			const downPayment = plan.insurance.downPayment
			if (typeof downPayment !== 'number') return

			const result = calculator.calculate({
				...baseScenario,
				downPayment,
			})

			if ('suggestions' in result) {
				const suggested = result.suggestions?.downPayment
				if (suggested !== undefined && suggested !== downPayment) {
					plan.insurance.downPayment = suggested
				}
			}
		}

		// Check if the down payment is disabled based on payment type, term, and funeral amount
		const isDownPaymentDisabled = computed(() => {
			const paymentType = plan.insurance?.paymentType
			const term = plan.insurance?.term
			const funeralAmount = localFuneralAmount.value ?? baseAmount.value ?? 0

			if (funeralAmount < 1000) return true

			return paymentType === PaymentType.SP_EFT || term === Term.SinglePay
		})

		// If the down payment is disabled, clear the value
		watch(
			isDownPaymentDisabled,
			(disabled) => {
				if (disabled && plan.insurance.downPayment !== undefined) {
					plan.insurance.downPayment = undefined
					getNode('downPayment')?.input(undefined)
				}
			},
			{ immediate: true }
		)

		// Set payment frequency disabled based on payment type, term, and if the funeral home is allowed to sell single pay
		const isPaymentFrequencyDisabled = computed(() => {
			const paymentType = plan.insurance.paymentType
			const isAllowedToSellSinglePayCc =
				sessionStore.activeFh?.isAllowedToSellSinglePayCc

			if (paymentType === PaymentType.CREDIT_CARD) {
				return !isAllowedToSellSinglePayCc
			}

			if (
				plan.insurance.term === Term.SinglePay ||
				paymentType === PaymentType.MP_EFT
			) {
				return true
			}

			return false
		})

		/** All our parameters, grouped and ready for the calculator */
		const scenario = computed((): Scenario | undefined => {
			mainErrors.value = []
			const state = sessionStore.activeFh?.billingAddress?.stateCode
			const product = sessionStore.activeFh?.insuranceProduct

			let age: number = 0

			if (!plan.birthDate) {
				console.warn('No birth date')
				idempotentPush(
					mainErrors.value,
					t('paymentOptionsSlide.errors.noBirthDate')
				)
			} else {
				age = getAge(plan.birthDate)
			}

			if (planHasNoCartItems.value) {
				console.warn('No cart items')
				idempotentPush(mainErrors.value, cartItemsError)
			}

			if (!Object.values(State).includes(state as State)) {
				console.warn(
					'Funeral home is not configured with a valid state:',
					state,
					Object.values(State)
				)
				idempotentPush(
					mainErrors.value,
					t('paymentOptionsSlide.errors.invalidFuneralHomeState')
				)
			}

			if (!product) {
				console.warn('Funeral home is not configured with a valid product')
				idempotentPush(
					mainErrors.value,
					t('paymentOptionsSlide.errors.invalidInsuranceProduct')
				)
			}

			// Set some sensible defaults
			if (!plan.insurance.paymentFrequency) {
				plan.insurance.paymentFrequency = Frequency.Monthly
			}

			if (!plan.insurance.paymentType) {
				plan.insurance.paymentType = PaymentType.MP_EFT
			}

			let funeralAmount = localFuneralAmount.value ?? baseAmount.value

			if (Object.keys(mainErrors.value).length > 0) {
				return undefined
			}

			return {
				age,
				state: state as State,

				product: product!.code,
				bump: product!.bump,

				funeralAmount: funeralAmount,
				downPayment: plan.insurance.downPayment ?? 0,
				paymentFrequency:
					(plan.insurance.paymentFrequency as Frequency) ?? Frequency.OneTime,
				term: plan.insurance.term ?? Term.SinglePay,
				terminallyIll: plan.insurance.terminallyIll,
				otherIllness: plan.insurance.otherIllness,

				paymentType: plan.insurance.paymentType as PaymentType,
			}
		})

		/** Push a value to an array, unless the array already has that value */
		function idempotentPush<T>(arr: Array<T>, value: T) {
			if (!arr.includes(value)) {
				arr.push(value)
			}
		}

		/** The age of the person we are planning for */
		function getAge(birthDate: string) {
			return Math.floor(
				DateTime.now().diff(DateTime.fromISO(birthDate), 'years').years
			)
		}

		/** Get the result of a scenario that potentially differs from the main scenario */
		function calculatePayment(
			overrides: Partial<Scenario>
		): ScenarioOutput | ScenarioErrorBag | undefined {
			if (!scenario.value) return undefined

			const modifiedScenario = {
				...scenario.value,
				...overrides,
			}

			const output = calculator.calculate(modifiedScenario)

			return output
		}

		type PaymentTermsOptionsOutput = {
			termOptions: TermOption[]
			errors: string[]
		}

		/** Available payment terms, i.e. lengths for the contract */
		const paymentTermOptions = computed((): PaymentTermsOptionsOutput => {
			const errors: string[] = []

			if (!scenario.value) {
				return {
					termOptions: [],
					errors: [],
				}
			}

			const selectedFreq = plan.insurance.paymentFrequency
			const selectedPaymentType = plan.insurance.paymentType

			const termOptions = calculator
				.getValidTerms(scenario.value)
				.map((term) => {
					const isSinglePayOrTerminal =
						term === Term.SinglePay || term === Term.TerminalIllness

					const overrides: Partial<Scenario> = {
						term,
						paymentType: isSinglePayOrTerminal
							? PaymentType.SP_EFT
							: selectedPaymentType !== PaymentType.SP_EFT
								? selectedPaymentType
								: PaymentType.MP_EFT,
						paymentFrequency: isSinglePayOrTerminal
							? Frequency.OneTime
							: selectedFreq !== Frequency.OneTime
								? selectedFreq
								: Frequency.Monthly,
					}

					const output = calculatePayment(overrides)

					if (!output || output instanceof ScenarioErrorBag) {
						if (output instanceof ScenarioErrorBag) {
							output.messages.forEach((m) =>
								idempotentPush(
									errors,
									m.message ??
										t('paymentOptionsSlide.errors.unknownScenarioError')
								)
							)
						}
						// Don't show term if there's an error
						return null
					}

					const oap = oapIncluded.value ? oapPrice.value : 0
					const everplans = everplansIncluded.value ? everplansPrice.value : 0

					const payment = isSinglePayOrTerminal
						? output.totalDueTodayAmt + oap + everplans
						: output.multiPayPeriodicAmt

					return {
						term,
						label: getTermLabel(term),
						payment,
						formattedPayment: formatPrice(payment),
					}
				})
				.filter((opt): opt is TermOption => opt !== null)

			return {
				termOptions,
				errors,
			}
		})

		/** Get the default multipay term up to 10 years, i.e. the longest term that isn't Single Pay or Terminal Illness
		 * If there are no multipay terms return single pay
		 */
		function getDefaultMultipayTerm(): Term | null {
			const validTerms = paymentTermOptions.value.termOptions.map(
				(opt) => opt.term
			)

			// Get the longest term up to 10 years that isn't Single Pay or Terminal Illness
			const multipayTerms = validTerms.filter(
				(t) =>
					![Term.SinglePay, Term.TerminalIllness].includes(t) &&
					t <= Term.TenYear
			)

			// If there are any multipay terms, return the longest
			if (multipayTerms.length > 0) {
				return multipayTerms.reduce((max, t) => (t > max ? t : max))
			}

			// Return SinglePay if there are no multipay terms available
			if (validTerms.includes(Term.SinglePay)) {
				return Term.SinglePay
			}

			return null
		}

		/** Update the term of the plan */
		function updateTerm(newTerm: Term) {
			plan.insurance.term = newTerm
		}

		/** Determine if this is a single payment plan */
		const singlePay = computed(
			() =>
				plan.insurance.term !== undefined &&
				[Term.SinglePay, Term.TerminalIllness].includes(plan.insurance.term)
		)

		/** Watch targetPayment and payment frequency and update targetTotal accordingly
		 * If the payment type is Direct Bill, adjust the payment based on the frequency so that
		 * it is equivalent to a monthly payment
		 */
		watch(targetPayment, (newVal) => {
			if (typeof newVal !== 'number' || isNaN(newVal) || !scenario.value) {
				targetTotal.value = null
				return
			}

			let adjustedPayment = newVal

			// Adjust based on Direct Bill frequency
			if (plan.insurance.paymentType === PaymentType.BILL) {
				switch (plan.insurance.paymentFrequency) {
					case Frequency.Quarterly:
						adjustedPayment = newVal / 3 // convert quarterly to monthly equivalent
						break
					case Frequency.SemiAnnual:
						adjustedPayment = newVal / 6 // semi-annual to monthly
						break
					case Frequency.Annual:
						adjustedPayment = newVal / 12 // annual to monthly
						break
					default:
						// assume monthly if not otherwise specified
						adjustedPayment = newVal
				}
			}

			const estimated = calculator.getDesiredFuneralAmount(
				scenario.value,
				adjustedPayment
			)
			targetTotal.value = typeof estimated === 'number' ? estimated : null
		})

		/**
		 * WATCH: When payment type changes
		 *
		 * Set the payment frequency and term based on the payment type
		 * SinglePay: OneTime, SinglePay
		 * Multipay: Monthly, default multipay term
		 * CreditCard: OneTime, SinglePay, or Monthly, current term
		 * Bill: Monthly, default multipay term
		 * Also clear down payment if payment type changes to single pay because down payment is not allowed with single pay
		 */
		watch(
			() => plan?.insurance.paymentType,
			(newPaymentType) => {
				if (!plan) return
				const insurance = plan.insurance
				const defaultTerm = getDefaultMultipayTerm()

				if (newPaymentType === PaymentType.SP_EFT) {
					insurance.paymentFrequency = Frequency.OneTime
					insurance.term = Term.SinglePay
					insurance.downPayment = undefined
				} else if (newPaymentType === PaymentType.MP_EFT) {
					insurance.paymentFrequency = Frequency.Monthly

					if (
						insurance.term === Term.SinglePay ||
						insurance.term === Term.TerminalIllness ||
						!insurance.term
					) {
						if (defaultTerm !== null) {
							insurance.term = defaultTerm
						}
					}
				} else if (newPaymentType === PaymentType.CREDIT_CARD) {
					if (!sessionStore.activeFh?.isAllowedToSellSinglePayCc) {
						insurance.paymentFrequency = Frequency.Monthly
					} else if (insurance.term === Term.SinglePay || !insurance.term) {
						insurance.paymentFrequency = Frequency.OneTime
						insurance.term = Term.SinglePay
					} else {
						insurance.paymentFrequency = Frequency.Monthly
					}
				} else if (newPaymentType === PaymentType.BILL) {
					insurance.paymentFrequency = Frequency.Monthly

					if (
						insurance.term === Term.SinglePay ||
						insurance.term === Term.TerminalIllness ||
						!insurance.term
					) {
						if (defaultTerm !== null) {
							insurance.term = defaultTerm
						}
					}
				}
			}
		)

		/**
		 * WATCH: When the insurance payment frequency changes
		 *
		 * If the payment frequency changes to OneTime and the payment type is CreditCard,
		 * set the term to SinglePay.
		 * If the payment frequency changes from OneTime to Monthly and the payment type is CreditCard,
		 * set the term to the default multipay term.
		 * Clear the target payment and total when frequency changes as well.
		 */

		watch(
			() => plan?.insurance.paymentFrequency,
			(newFrequency) => {
				if (!plan) return
				const insurance = plan.insurance

				if (
					newFrequency === Frequency.OneTime &&
					insurance.paymentType === PaymentType.CREDIT_CARD
				) {
					insurance.term = Term.SinglePay
				} else if (
					insurance.paymentType === PaymentType.CREDIT_CARD &&
					insurance.term === Term.SinglePay &&
					newFrequency !== Frequency.OneTime
				) {
					const multipay = getDefaultMultipayTerm()
					if (multipay) {
						insurance.term = multipay
					}
				}

				// Clear target payment and total if leaving monthly
				targetPayment.value = undefined
				targetTotal.value = null
			}
		)

		/**
		 * WATCH: When the insurance term changes
		 *
		 * Adjusts the payment type and frequency based on the new term
		 * SinglePay or TerminalIllness term: Set to SinglePay EFT, OneTime frequency
		 * Otherwise: Set to MultiPay EFT, Monthly frequency if insurance payment type is unset or SinglePay EFT
		 * If the term is TerminalIllness, set the OAP payment frequency to PayInFull
		 */
		watch(
			() => plan?.insurance.term,
			(newTerm) => {
				if (!plan) return
				const insurance = plan.insurance

				if (
					newTerm !== undefined &&
					[Term.SinglePay, Term.TerminalIllness].includes(newTerm)
				) {
					// SinglePay or TerminalIllness logic
					if (
						insurance.paymentType !== PaymentType.CREDIT_CARD ||
						(insurance.paymentType === PaymentType.CREDIT_CARD &&
							!sessionStore.activeFh?.isAllowedToSellSinglePayCc)
					) {
						insurance.paymentType = PaymentType.SP_EFT
					}
					insurance.paymentFrequency = Frequency.OneTime

					// Clear down payment because it is not allowed for single term ot terminally ill
					insurance.downPayment = undefined

					if (
						newTerm === Term.TerminalIllness &&
						plan.oapPayment.paymentPlanId
					) {
						plan.oapPayment.paymentPlanId =
							oapPlanProvider.getPlanByPaymentCount(1)?.id ?? null
					}
				} else {
					// Multi-pay term logic

					// If payment type is CreditCard and frequency is OneTime, set to Monthly
					if (
						insurance.paymentType === PaymentType.CREDIT_CARD &&
						insurance.paymentFrequency === Frequency.OneTime
					) {
						insurance.paymentFrequency = Frequency.Monthly
					}

					// If payment type is SinglePay EFT, set to MultiPay EFT
					if (
						insurance.paymentType === PaymentType.SP_EFT ||
						!insurance.paymentType
					) {
						insurance.paymentType = PaymentType.MP_EFT
						insurance.paymentFrequency = Frequency.Monthly
					}
				}
			}
		)

		/**
		 * Hide the adjust inputs when the payment type is SinglePay or the term changes to a single pay term
		 */
		watch(
			() => [singlePay.value, plan?.insurance?.paymentType],
			([isSinglePay, paymentType]) => {
				if (isSinglePay || paymentType === PaymentType.SP_EFT) {
					showAdjustInputs.value = false
				}
			}
		)

		/**
		 * Select a valid term when the birth date changes
		 * If there are no cart items, do not update the term
		 * If there is no term selected yet, do not update the term
		 */
		watch(
			() => plan.birthDate,
			(newVal, oldVal) => {
				if (!newVal || newVal === oldVal) return
				if (planHasNoCartItems) return
				if (!plan.insurance.term) return

				const defaultTerm = getDefaultMultipayTerm()

				// Safe fallback access
				const fallbackTerm = paymentTermOptions.value?.termOptions?.[0]?.term

				if (defaultTerm || fallbackTerm) {
					updateTerm(defaultTerm ?? fallbackTerm)
				}
			}
		)

		/**
		 * Keep the plan's selected term in sync with the available term options.
		 * If cart items exist and the current term is no longer among the available options (like if birth date changes),
		 * update it to the default multipay term or fall back to the first available term.
		 */
		watch(
			() => [
				plan.cartItems?.length,
				paymentTermOptions.value.termOptions,
				plan.insurance.term,
			],
			() => {
				const terms = paymentTermOptions.value.termOptions
				const cartLength = plan.cartItems?.length

				if (
					plan.insurance.term &&
					cartLength > 0 &&
					terms.length > 0 &&
					!terms.some((o) => o.term === plan.insurance.term)
				) {
					const defaultTerm = getDefaultMultipayTerm()
					const availableTerm = terms[0]?.term
					updateTerm(defaultTerm ?? availableTerm)
				}
			}
		)

		/** Determine the payment summary options based on oap inclusion, payment type, and payment frequency of both the payment plan and oap */
		const paymentSummaryOptions = computed(() => {
			const options: { label: string; amount: number }[] = []

			const baseSingle =
				scenarioOutput.value &&
				!(scenarioOutput.value instanceof ScenarioErrorBag)
					? scenarioOutput.value.totalDueTodayAmt
					: 0

			const baseMulti =
				scenarioOutput.value &&
				!(scenarioOutput.value instanceof ScenarioErrorBag)
					? scenarioOutput.value.multiPayPeriodicAmt
					: 0

			const oap = oapPrice.value ?? 0
			const everplans = everplansPrice.value ?? 0

			if (singlePay.value) {
				let dueToday = baseSingle
				if (oapIncluded.value) dueToday += oap
				if (plan.everplansIncluded) dueToday += everplans

				options.push({
					label: t('payment.dueToday'),
					amount: dueToday,
				})

				if (isMonthlyOap.value && oapIncluded.value) {
					options.push({
						label: t('payment.frequencyPaymentTitle', {
							frequency: monthlyTranslated.value,
							label: `(${twelveMonthsTranslated.value})`,
						}),
						amount: oap,
					})
				}

				if (isThreeMonthOap.value && oapIncluded.value) {
					const label =
						plan.insurance.paymentFrequency === Frequency.Monthly
							? t('payment.frequencyPaymentTitle', {
									frequency: monthlyTranslated.value,
									label: `(${firstThreeMonthsTranslated.value})`,
								})
							: t('payment.frequencyPaymentTitle', {
									// todo: figure out what the frequency is for
									frequency: monthlyTranslated.value,
									label: `(${threeMonthsTranslated.value})`,
								})

					options.push({
						label,
						amount: oap,
					})
				}
			} else {
				let dueToday = baseMulti

				if (oapIncluded.value) {
					dueToday += oap
				}

				if (everplansIncluded.value) {
					dueToday += everplans
				}

				if (plan.insurance.downPayment) {
					dueToday += plan.insurance.downPayment
					dueToday -= monthlyPayment.value ?? 0
				}

				// Only push Due Today if OAP (pay in full) or Everplans is included
				if (
					(oapIncluded.value && isPayInFullOap.value) ||
					everplansIncluded.value
				) {
					options.push({
						label: t('payment.dueToday'),
						amount: dueToday,
					})
				}

				if (isMonthlyOap.value && oapIncluded.value) {
					if (plan.insurance.paymentFrequency === Frequency.Monthly) {
						options.push({
							label: t('payment.frequencyPaymentTitle', {
								frequency: monthlyTranslated.value,
								label: `(${firstYearTranslated.value})`,
							}),
							amount: baseMulti + oap,
						})
					} else {
						options.push({
							label: t('payment.frequencyPaymentTitle', {
								frequency: monthlyTranslated.value,
								label: `(${twelveMonthsTranslated.value})`,
							}),
							amount: oap,
						})
					}
				} else if (isThreeMonthOap.value && oapIncluded.value) {
					const label =
						plan.insurance.paymentFrequency === Frequency.Monthly
							? t('payment.frequencyPaymentTitle', {
									frequency: monthlyTranslated.value,
									label: `(${firstThreeMonthsTranslated.value})`,
								})
							: t('payment.frequencyPaymentTitle', {
									frequency: monthlyTranslated.value,
									label: `(${threeMonthsTranslated.value})`,
								})

					if (plan.insurance.paymentFrequency === Frequency.Monthly) {
						options.push({
							label,
							amount: baseMulti + oap,
						})
					} else {
						options.push({
							label,
							amount: oap,
						})
					}
				}

				// If OAP is included and the payment type is Bill determine the label
				let ongoingLabel = t('payment.frequencyPaymentTitle', {
					frequency: monthlyTranslated.value,
					label: `(${ongoingTranslated.value})`,
				})
				if (plan.insurance.paymentType === PaymentType.BILL) {
					switch (plan.insurance.paymentFrequency) {
						case Frequency.Quarterly:
							ongoingLabel = t('payment.frequencyPaymentTitle', {
								frequency: quarterlyTranslated.value,
								label: `(${ongoingTranslated.value})`,
							})
							break
						case Frequency.SemiAnnual:
							ongoingLabel = t('payment.frequencyPaymentTitle', {
								frequency: semiAnnualTranslated.value,
								label: `(${ongoingTranslated.value})`,
							})
							break
						case Frequency.Annual:
							ongoingLabel = t('payment.frequencyPaymentTitle', {
								frequency: annualTranslated.value,
								label: `(${ongoingTranslated.value})`,
							})
							break
					}
				}

				// If OAP is not included and the payment type is Bill determine the label without 'Ongoing'
				if (!oapIncluded.value) {
					if (plan.insurance.paymentType === PaymentType.BILL) {
						switch (plan.insurance.paymentFrequency) {
							case Frequency.Quarterly:
								ongoingLabel = t('payment.frequencyPaymentTitle', {
									frequency: 'Quarterly',
									label: '',
								})
								break
							case Frequency.SemiAnnual:
								ongoingLabel = t('payment.frequencyPaymentTitle', {
									frequency: 'Semi-Annual',
									label: '',
								})
								break
							case Frequency.Annual:
								ongoingLabel = t('payment.frequencyPaymentTitle', {
									frequency: 'Annual',
									label: '',
								})
								break
							default:
								ongoingLabel = t('payment.frequencyPaymentTitle', {
									frequency: monthlyTranslated.value,
									label: '',
								})
						}
					} else {
						ongoingLabel = t('payment.frequencyPaymentTitle', {
							frequency: monthlyTranslated.value,
							label: '',
						})
					}
				}

				options.push({
					label: ongoingLabel,
					amount: monthlyPayment.value ?? 0,
				})
			}

			return options
		})

		const paymentTypeOptions = computed(() => [
			{
				label: t('payment.paymentTypeOptions.singlePayEft'),
				value: PaymentType.SP_EFT,
			},
			{
				label: t('payment.paymentTypeOptions.multiPayEft'),
				value: PaymentType.MP_EFT,
				attrs: { disabled: isTerminalIllness.value },
			},
			{
				label: t('payment.paymentTypeOptions.directBill'),
				value: PaymentType.BILL,
				attrs: { disabled: isTerminalIllness.value },
			},
			{
				label: t('payment.paymentTypeOptions.creditCard'),
				value: PaymentType.CREDIT_CARD,
				attrs: { disabled: isTerminalIllness.value },
			},
		])

		const paymentFrequencyOptions = computed(() => {
			const isTerminal = isTerminalIllness.value
			const isAllowedToSellSinglePayCc =
				sessionStore.activeFh?.isAllowedToSellSinglePayCc ?? false
			const paymentType = selectedPaymentType.value

			return [
				{
					label: t('payment.paymentFrequencyOptions.payInFull'),
					value: Frequency.OneTime,
					attrs: {
						disabled:
							isTerminal ||
							paymentType === PaymentType.BILL ||
							paymentType === PaymentType.MP_EFT ||
							!isAllowedToSellSinglePayCc,
					},
				},
				{
					label: t('payment.paymentFrequencyOptions.monthly'),
					value: Frequency.Monthly,
					attrs: {
						disabled: isTerminal || paymentType === PaymentType.SP_EFT,
					},
				},
				{
					label: t('payment.paymentFrequencyOptions.quarterly'),
					value: Frequency.Quarterly,
					attrs: {
						disabled:
							isTerminal ||
							paymentType === PaymentType.CREDIT_CARD ||
							paymentType === PaymentType.MP_EFT ||
							paymentType === PaymentType.SP_EFT,
					},
				},
				{
					label: t('payment.paymentFrequencyOptions.semiAnnual'),
					value: Frequency.SemiAnnual,
					attrs: {
						disabled:
							isTerminal ||
							paymentType === PaymentType.CREDIT_CARD ||
							paymentType === PaymentType.MP_EFT ||
							paymentType === PaymentType.SP_EFT,
					},
				},
				{
					label: t('payment.paymentFrequencyOptions.annual'),
					value: Frequency.Annual,
					attrs: {
						disabled:
							isTerminal ||
							paymentType === PaymentType.CREDIT_CARD ||
							paymentType === PaymentType.MP_EFT ||
							paymentType === PaymentType.SP_EFT,
					},
				},
			]
		})

		const oapPaymentFrequencyOptions = computed(() => {
			const isTerminallyIll = plan?.insurance?.term === Term.TerminalIllness
			const price = mockProductOap.value?.fixedPrice
			const hasThreeMonth = price === 499 || price === 549

			const options = [
				{
					label: t('payment.paymentFrequencyOptions.payInFull'),
					value: 1,
				},
				{
					label: t('payment.paymentFrequencyOptions.monthly'),
					value: 12,
					attrs: { disabled: isTerminallyIll },
				},
				{
					label: t('payment.paymentFrequencyOptions.threeMonths'),
					value: 3,
					attrs: {
						disabled: isTerminallyIll || !hasThreeMonth,
					},
				},
			]

			// Carriage only offers single pay and 12 month OAP plans
			return isCarriageFuneralHome.value
				? options.filter((option) => option.value !== 3)
				: options
		})

		return {
			baseAmount,
			downPaymentRule,
			errors: mainErrors,
			everplansPrice,
			firstPaymentAmount,
			funeralAmountInput,
			funeralAmountRule,
			handleDownPaymentBlur,
			handleFuneralAmountBlur,
			isDownPaymentDisabled,
			isPaymentFrequencyDisabled,
			localFuneralAmount,
			monthlyPayment,
			oapPaymentFrequencyOptions,
			oapPrice,
			paymentFrequencyOptions,
			paymentSummaryOptions,
			paymentTermOptions,
			paymentTypeOptions,
			scenario,
			scenarioOutput,
			selectedPaymentType,
			singlePay,
			targetPayment,
			targetTotal,
			totalDueTodayAmount,
			totalFuneralAmount,
			totalPaidOverTime,
			wasEverplansInitiallyIncluded,
			wasOapInitiallyIncluded,
		}
	}
	return {
		getPlanPaymentOptions,
	}
}
